/**
 * A single, once-evaluated read of "is this a touch device?".
 *
 * Palimpsest branches on this in exactly a few places — the input layer swaps
 * pointer-lock for an on-screen overlay, the renderer relaxes its pixel-ratio and
 * shadow budget, and the veil hints change wording. Evaluating it once (module load)
 * keeps every consumer a plain `if (IS_TOUCH)` with no per-frame cost and no risk of
 * the answer changing mid-session.
 *
 * Detection is deliberately conservative: a real touch capability AND a coarse
 * pointer. That excludes touch-capable laptops driven by a mouse/trackpad (which
 * should keep the desktop scheme) while still catching phones and tablets. If
 * `matchMedia` is missing — some embedded webviews omit it — we fall back to raw
 * touch capability so those environments still get the overlay rather than an
 * unplayable pointer-lock page.
 */
const hasTouch =
  'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;

const mediaQuery =
  typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;

const coarsePointer = mediaQuery ? mediaQuery.matches : false;

/** `?touch` forces the touch scheme on any device — a test/debug affordance so the
 *  overlay can be exercised (and demoed) from a desktop browser. */
const forced = new URLSearchParams(window.location.search).has('touch');

export const IS_TOUCH: boolean = forced || (hasTouch && (coarsePointer || mediaQuery === null));
